import type { ReactElement } from "react";

import type { BookExposureView } from "../api/client.js";

/**
 * Where the money sits, and which accounts are carrying the wear.
 *
 * Books limit on profitability and pattern rather than volume, so return per
 * book is the early warning that an account is about to close - and the
 * accounts worth worrying about are the ones hardest to replace. Two books
 * price almost the whole player-prop market and the profitable rules are both
 * props; three price no props at all and are effectively expendable. Losing
 * the wrong one does not trim the edge, it removes it.
 *
 * So scarcity is shown next to turnover rather than left implicit: 32% of
 * turnover at an expendable book is fine, and the same share at an
 * irreplaceable one is the thing to act on.
 */
const money = (cents: number): string =>
  `${cents < 0 ? "-" : ""}$${(Math.abs(cents) / 100).toFixed(2)}`;

const percent = (value: number): string => `${(value * 100).toFixed(0)}%`;

function scarcityLabel(scarcity: number): string {
  if (scarcity >= 0.2) return "irreplaceable";
  if (scarcity >= 0.05) return "some props";
  return "expendable";
}

export function BookExposure({
  books,
  onStatusChange,
}: {
  readonly books: readonly BookExposureView[];
  readonly onStatusChange?: (
    bookmaker: string,
    status: "ACTIVE" | "LIMITED" | "CLOSED",
  ) => void;
}): ReactElement {
  const totalAvailable = books.reduce(
    (sum, book) => sum + book.availableCents,
    0,
  );
  const totalPending = books.reduce((sum, book) => sum + book.pendingCents, 0);

  return (
    <section className="card" aria-label="Book exposure">
      <div className="section-heading">
        <h3>Book exposure</h3>
        <span>
          {money(totalAvailable)} available, {money(totalPending)} pending
        </span>
      </div>
      <div className="table-shell">
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">Book</th>
              <th scope="col">Available</th>
              <th scope="col">Pending</th>
              <th scope="col">30d turnover</th>
              <th scope="col">Return</th>
              <th scope="col">Role</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {books.length === 0 ? (
              <tr>
                <td colSpan={7}>No bookmaker accounts recorded yet.</td>
              </tr>
            ) : (
              books.map((book) => (
                <tr
                  key={book.bookmaker}
                  className={book.status !== "ACTIVE" ? "row-muted" : undefined}
                >
                  <th scope="row">
                    {book.bookmaker}
                    {book.pricesFrom ? (
                      <span className="muted"> · {book.pricesFrom} board</span>
                    ) : null}
                  </th>
                  <td
                    className={book.lowBalance ? "warn" : undefined}
                    data-label="Available"
                  >
                    {money(book.availableCents)}
                    {book.lowBalance ? " · low" : ""}
                  </td>
                  <td data-label="Pending">{money(book.pendingCents)}</td>
                  <td data-label="30d turnover">
                    {money(book.turnoverCents)}
                    <span className="muted">
                      {" "}
                      · {percent(book.turnoverShare)}
                    </span>
                  </td>
                  <td data-label="Return">
                    {book.roiPct === null
                      ? "—"
                      : `${book.roiPct > 0 ? "+" : ""}${book.roiPct.toFixed(1)}%`}
                    <span className="muted"> · {book.bets} bets</span>
                  </td>
                  <td data-label="Role">
                    {scarcityLabel(book.scarcity)}
                    <span className="muted">
                      {" "}
                      · {percent(book.scarcity)} of props
                    </span>
                  </td>
                  <td data-label="Status">
                    {onStatusChange === undefined ? (
                      book.status
                    ) : (
                      <select
                        aria-label={`${book.bookmaker} status`}
                        value={book.status}
                        onChange={(event) =>
                          onStatusChange(
                            book.bookmaker,
                            event.target.value as
                              "ACTIVE" | "LIMITED" | "CLOSED",
                          )
                        }
                      >
                        <option value="ACTIVE">Active</option>
                        <option value="LIMITED">Limited</option>
                        <option value="CLOSED">Closed</option>
                      </select>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
